import type { ClassValue } from 'clsx';
import type { ReactNode } from 'react';

import { clsxm } from '@vs-shop/utils';
import { ClampedText } from '@vs-shop/ui/clampedText';

import {
  ProductCard as ProductCardSection,
  ProductCardDescription,
  ProductCardFooter,
  ProductCardPromo,
  type ProductCardVariant,
} from '@vs-shop/commerce-ui/productCard';

type Props = {
  variant: ProductCardVariant;
  /** The name block — top of the card, above the imagery. */
  header: ReactNode;
  /** What fills the imagery box: the photo and whatever hangs over it. */
  media: ReactNode;
  /** The promotional strip under the imagery. The strip keeps its height when this renders nothing. */
  promo: ReactNode;
  /** The supporting copy. Clamped to three lines here, so no composing site restates that number. */
  description: ReactNode;
  /** The bottom block: price and availability, then whatever acts on the product. */
  footer: ReactNode;
  className?: ClassValue;
};

/**
 * rcprofi's arrangement of the shared product-card section.
 *
 * The anatomy — which parts exist and how each paints itself — is
 * `@vs-shop/commerce-ui`'s. What stays here is the ARRANGEMENT: which shared
 * wrapper each region sits in, the order of the regions, and the two pieces of
 * geometry that are this tenant's rather than the section's — the element that
 * groups the name with the imagery so `justify-between` drops the footer to the
 * bottom of the cell, and the three-line clamp on the copy.
 *
 * The BINDING is not here. Each region arrives as a slot, so this component
 * names no domain type and reads no product field. A composing site holds the
 * card's data and hands each region the part that renders it — rule 21
 * (`.agents/rules/21-prop-contract.md`): a component whose honest field set is
 * large should not receive data at all, and its content belongs in the composing
 * JSX. Flattening this component instead produced a fifteen-scalar signature at
 * seven call sites; issue 225 weighed the two and the composition won.
 *
 * The recommendation appearance replaces the action row with an inline
 * add-to-cart control, which is a different arrangement rather than a different
 * part: the footer holds the price row plus whatever acts on the product. That
 * choice belongs to the composing site, which knows the appearance it asked
 * for, so the footer slot carries both in the order they render.
 *
 * `ProductCardSkeleton` composes the same wrappers in the same order for the
 * loading state.
 */
export default function ProductCard({
  variant,
  header,
  media,
  promo,
  description,
  footer,
  className,
}: Props) {
  return (
    <ProductCardSection variant={variant} className={clsxm(className)}>
      <div>
        {header}

        {/* COVER IMAGE, VARIANTS */}
        {media}
      </div>

      {/* DISCOUNT CODE */}
      <ProductCardPromo>{promo}</ProductCardPromo>

      {/* DESCRIPTION */}
      <ProductCardDescription variant={variant}>
        <ClampedText lines={3}>{description}</ClampedText>
      </ProductCardDescription>

      {/* FOOTER */}
      <ProductCardFooter variant={variant}>{footer}</ProductCardFooter>
    </ProductCardSection>
  );
}
